import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/Avatar';
import { cn } from '@/lib/utils';
import { formatRelative, formatDateTime } from '@/lib/formatters';
import { initialsOf } from '@/components/projects/PeoplePicker';
import type { TaskStatus } from '@/types/database';
import { TICKET_STATUS_META, ticketRef } from '@/features/tickets/model';
import {
  isTicketNotification,
  notificationHeadline,
  notificationHref,
  notificationShowsSnippet,
  useDeleteNotification,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  type NotificationListFilter,
  type NotificationWithContext,
} from '@/features/notifications/hooks';

const STATUS_META: Record<TaskStatus, { label: string; className: string }> = {
  not_started: {
    label: 'Not started',
    className:
      'border border-dashed border-border bg-transparent text-muted-foreground',
  },
  waiting: { label: 'Waiting', className: 'bg-muted text-muted-foreground' },
  in_progress: { label: 'In progress', className: 'bg-atmosphere-dark text-linen' },
};

// Order used when sorting by task status (roughly "least → most active").
const STATUS_ORDER: Record<TaskStatus, number> = {
  not_started: 0,
  waiting: 1,
  in_progress: 2,
};

type SortMode = 'newest' | 'status';
type KindFilter = 'all' | 'tickets' | 'mentions';

function statusRank(n: NotificationWithContext): number {
  if (n.task) return STATUS_ORDER[n.task.status];
  if (n.ticket) return 10 + TICKET_STATUS_META[n.ticket.status].order;
  return 99;
}

export function NotificationsPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<NotificationListFilter>('all');
  const [kind, setKind] = useState<KindFilter>('all');
  const [sort, setSort] = useState<SortMode>('newest');

  const listQ = useNotifications(filter);
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();
  const del = useDeleteNotification();

  const items = useMemo(() => {
    let rows = [...(listQ.data ?? [])];
    if (kind === 'tickets') rows = rows.filter((n) => isTicketNotification(n));
    if (kind === 'mentions') rows = rows.filter((n) => !isTicketNotification(n));
    if (sort === 'status') {
      rows.sort((a, b) => {
        const sa = statusRank(a);
        const sb = statusRank(b);
        if (sa !== sb) return sa - sb;
        return b.created_at.localeCompare(a.created_at);
      });
    }
    return rows;
  }, [listQ.data, kind, sort]);

  const hasUnread = (listQ.data ?? []).some((n) => !n.read_at);

  const onOpen = (n: NotificationWithContext) => {
    if (!n.read_at) markRead.mutate({ id: n.id, read: true });
    navigate(notificationHref(n));
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notifications"
        actions={
          hasUnread ? (
            <Button
              variant="outline"
              onClick={() => markAll.mutate()}
              disabled={markAll.isPending}
            >
              <CheckCheck className="h-4 w-4" />
              Mark all read
            </Button>
          ) : undefined
        }
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1.5">
          <FilterChip
            label="All"
            active={filter === 'all'}
            onClick={() => setFilter('all')}
          />
          <FilterChip
            label="Unread"
            active={filter === 'unread'}
            onClick={() => setFilter('unread')}
          />
          <span className="mx-1 h-4 w-px bg-border" aria-hidden="true" />
          <FilterChip
            label="Everything"
            active={kind === 'all'}
            onClick={() => setKind('all')}
          />
          <FilterChip
            label="Tickets"
            active={kind === 'tickets'}
            onClick={() => setKind('tickets')}
          />
          <FilterChip
            label="Mentions"
            active={kind === 'mentions'}
            onClick={() => setKind('mentions')}
          />
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span>Sort by</span>
          <FilterChip
            label="Newest"
            active={sort === 'newest'}
            onClick={() => setSort('newest')}
          />
          <FilterChip
            label="Status"
            active={sort === 'status'}
            onClick={() => setSort('status')}
          />
        </div>
      </div>

      {listQ.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full rounded-lg" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border px-6 py-16 text-center">
          <Bell className="h-8 w-8 text-muted-foreground" strokeWidth={1.4} />
          <p className="text-sm font-medium text-foreground">
            {filter === 'unread' ? 'No unread notifications' : 'Nothing here yet'}
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((n) => (
            <NotificationRow
              key={n.id}
              n={n}
              onOpen={() => onOpen(n)}
              onToggleRead={() =>
                markRead.mutate({ id: n.id, read: !n.read_at })
              }
              onDelete={() => del.mutate(n.id)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function NotificationRow({
  n,
  onOpen,
  onToggleRead,
  onDelete,
}: {
  n: NotificationWithContext;
  onOpen: () => void;
  onToggleRead: () => void;
  onDelete: () => void;
}) {
  return (
    <li
      className={cn(
        'group flex items-start gap-3 rounded-lg border border-border bg-card px-4 py-3 transition-colors hover:border-foreground/20',
        !n.read_at && 'bg-atmosphere/10',
      )}
    >
      <Avatar className="mt-0.5 h-8 w-8 shrink-0">
        {n.actor?.avatar_url && <AvatarImage src={n.actor.avatar_url} alt="" />}
        <AvatarFallback className="text-[10px]">
          {n.actor ? initialsOf(n.actor) : '?'}
        </AvatarFallback>
      </Avatar>

      <button
        type="button"
        onClick={onOpen}
        className="min-w-0 flex-1 text-left"
      >
        <p className="text-sm text-foreground">
          <span className="font-medium">
            {n.actor?.full_name ?? n.actor?.email ?? 'Someone'}
          </span>{' '}
          <span className="text-muted-foreground">
            {notificationHeadline(n)}
          </span>
        </p>
        {n.ticket && (
          <p className="mt-1 truncate text-sm text-foreground/90">{n.ticket.title}</p>
        )}
        {notificationShowsSnippet(n) && (
          <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
            {n.snippet}
          </p>
        )}
        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {n.project && (
            <Badge variant="muted" className="rounded-full">
              {n.project.name}
            </Badge>
          )}
          {n.ticket && (
            <>
              <Badge variant="muted" className="rounded-full font-mono">
                {ticketRef(n.ticket.number)}
              </Badge>
              <span
                className={cn(
                  'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium',
                  TICKET_STATUS_META[n.ticket.status].className,
                )}
              >
                {TICKET_STATUS_META[n.ticket.status].label}
              </span>
            </>
          )}
          {n.task && (
            <span
              className={cn(
                'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium',
                STATUS_META[n.task.status].className,
              )}
            >
              {STATUS_META[n.task.status].label}
            </span>
          )}
          <span title={formatDateTime(n.created_at)}>
            {formatRelative(n.created_at)}
          </span>
        </div>
      </button>

      <div className="flex shrink-0 items-center gap-1">
        {!n.read_at && (
          <span
            className="mt-1 h-2 w-2 rounded-full bg-fresh-salmon-dark"
            aria-hidden="true"
          />
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
          onClick={onToggleRead}
          aria-label={n.read_at ? 'Mark as unread' : 'Mark as read'}
          title={n.read_at ? 'Mark as unread' : 'Mark as read'}
        >
          <Check className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-destructive"
          onClick={onDelete}
          aria-label="Dismiss"
          title="Dismiss"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </li>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full border px-3 py-1 text-xs transition-colors',
        active
          ? 'border-transparent bg-foreground text-background'
          : 'border-border bg-transparent text-muted-foreground hover:text-foreground',
      )}
    >
      {label}
    </button>
  );
}
